import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AmplitudeService } from '../../../shared/services/amplitude.service';
import { HttpExceptionService } from '../../../shared/services/http-exception.service';
import { InvalidTimeDialogComponent } from '../invalid-time-dialog/invalid-time-dialog.component';
import { Customer } from '../shared/interfaces/customer.model';
import { PageDataScheduleTypes } from '../shared/interfaces/page-data.enum';
import { 
  ScheduleRequest, 
  ScheduleRequestData 
} from '../shared/interfaces/schedule-request.model';
import { Schedule } from '../shared/interfaces/schedule.model';
import { PublicApiService } from '../shared/services/public-api.service';
import { PublicService } from '../shared/services/public.service';
import { trackingsAmplitude } from '../../../../../../component-library/src/lib/trackings-amplitude'

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss']
})
export class SummaryComponent implements OnInit, OnDestroy {

  private subscription: Subscription;

  publicScheduleData!: Schedule;
  form!: FormGroup;
  showPolitic: boolean = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private publicApiService: PublicApiService,
    private publicService: PublicService,
    private httpExceptionService: HttpExceptionService,
    private amplitudeService: AmplitudeService
  ) {
    this.subscription = new Subscription();
  }

  ngOnInit(): void {
    this.listenPublicScheduleData();
    this.listenInvalidTimeError();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private createForm(customer?: Customer) {
    if (!customer) {
      customer = { name: '', cellphone: '' };
    }
    this.form = new FormGroup({
      name: new FormControl(customer.name, [Validators.required]),
      cellphone: new FormControl(customer.cellphone, [Validators.required]),
      acceptTerms: new FormControl(false, [Validators.requiredTrue])
    })
  }

  private listenPublicScheduleData(): void {
    this.subscription.add(
      this.publicService.onPublicScheduleData.subscribe(x => {
        this.publicScheduleData = x;
        this.createForm(this.publicScheduleData.customer);
      })
    );
  }

  private listenInvalidTimeError(): void {
    this.subscription.add(
      this.httpExceptionService.onInvalidTimeError
        .pipe(filter(x => !!x))
        .subscribe(x => {
          this.openInvalidTimeDialog(x);
          this.httpExceptionService.clearInvalidTimeError();
        })
    );
  }

  private generatePublicScheduleData(): ScheduleRequest {
    const { clinic, dentist, schedule_type, available_hour, customer } = this.publicScheduleData;
    let schedule: ScheduleRequestData = {
      schedule_type_id: schedule_type.id,
      schedule_start: available_hour.start_time,
      clinic_id: clinic.id,
      chair_id: available_hour.chair_id,
      dentist_id: dentist.id
    }
    return { schedule, customer }
  }

  private savePublicSchedule() {
    const schedule: ScheduleRequest = this.generatePublicScheduleData();
    this.subscription.add(
      this.publicApiService.postPublicSchedule(schedule).subscribe(result => {
        this.publicService.setPublicScheduleSuccessData(result);
        this.publicService.clearPublicScheduleData();
        this.amplitudeLogEvent(schedule);
        this.redirectToSuccess();
      })
    );
  }

  private amplitudeLogEvent(schedule: ScheduleRequest): void {
    const data: {name: string, phoneNumber: string, clientKey: string | undefined} = {
      name: schedule.customer.name,
      phoneNumber: schedule.customer.cellphone,
      clientKey: this.publicScheduleData.client_key
    };
    this.amplitudeService.logEvent(trackingsAmplitude.AGENDAMENTO_ONLINE_PACIENTE, data);
  }

  private redirectToSuccess() {
    this.router.navigate(['confirmation'], { relativeTo: this.activatedRoute })
  }

  private openInvalidTimeDialog(errors: any) {
    const dialogRef = this.dialog.open(InvalidTimeDialogComponent, 
      { data: { errors }}
    );
    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(['../'], { relativeTo: this.activatedRoute })
    });
  }

  getScheduleTypeName(): string {
    return this.publicService.getTypeDescriptionByName(
      this.publicScheduleData.schedule_type?.name
    );
  }

  onClick() {
    this.publicService.setPublicScheduleSelectedData(
      PageDataScheduleTypes.CUSTOMER_SCHEDULE_DATA,
      this.form.value
    );
    this.savePublicSchedule();
  }

  onPoliticClick(): void {
    this.showPolitic = !this.showPolitic;
  }
}
